import {
  ComponentClass,
  ComponentProps,
  FunctionComponent,
  useContext,
} from "react";
import styled from "styled-components";
import { HorizontalLinearStepper } from "../../../components/stepper/horizontal";
import { StepperPropTypes } from "../../../components/stepper/models";
import { VerticalLinearStepper } from "../../../components/stepper/vertical";
import { ConfigContext } from "./config-context";

const InnerFormHorizontalLinearStepper = styled(
  ({ background, foreground, primaryCta, secondaryCta, ...props }) => (
    <HorizontalLinearStepper {...props} />
  )
)<{
  background?: string;
  foreground?: string;
  primaryCta?: string;
  secondaryCta?: string;
}>`
  .MuiStepper-root {
    ${(props) => props.background && `background: ${props.background}`};
    ${(props) => props.foreground && `color: ${props.foreground}`};
  }

  .MuiStepLabel-label {
    color: inherit;
  }

  .MuiStepIcon-text {
    ${(props) => props.background && `color: ${props.background}`};
  }

  .MuiStepIcon-root {
    ${(props) => props.primaryCta && `color: ${props.primaryCta}`};

    &.MuiStepIcon-active {
      border: 2px solid ${(props) => props.primaryCta || "inherit"};
      border-radius: 100%;
      ${(props) => props.secondaryCta && `color: ${props.secondaryCta}`};

      .MuiStepIcon-text {
        ${(props) => props.primaryCta && `fill: ${props.primaryCta}`};
      }
    }
  }
`;

const InnerFormVerticalLinearStepper = styled(
  ({ background, foreground, primaryCta, secondaryCta, ...props }) => (
    <VerticalLinearStepper {...props} />
  )
)<{
  background?: string;
  foreground?: string;
  primaryCta?: string;
  secondaryCta?: string;
}>`
  &,
  .MuiPaper-root {
    ${(props) => props.background && `background-color: ${props.background}`};
    ${(props) => props.foreground && `color: ${props.foreground}`};
  }

  .MuiStepLabel-label {
    color: inherit;
  }

  .MuiStepIcon-text {
    ${(props) => props.background && `color: ${props.background}`};
  }

  .MuiStepIcon-root {
    ${(props) => props.primaryCta && `color: ${props.primaryCta}`};

    &.MuiStepIcon-active {
      border: 2px solid ${(props) => props.primaryCta || "inherit"};
      border-radius: 100%;
      ${(props) => props.secondaryCta && `color: ${props.secondaryCta}`};

      .MuiStepIcon-text {
        ${(props) => props.primaryCta && `fill: ${props.primaryCta}`};
      }
    }
  }
`;

export function FormHorizontalLinearStepper<
  T extends {
    content:
      | FunctionComponent<A>
      | ComponentClass<A, any>
      | ((x: A) => JSX.Element);
  },
  A extends {} = ComponentProps<T["content"]>
>(props: StepperPropTypes<T, A>) {
  const { config } = useContext(ConfigContext);
  return (
    <InnerFormHorizontalLinearStepper
      background={config.configuration.css.formBackgroundColor}
      foreground={config.configuration.css.textColor}
      primaryCta={config.configuration.css.buttonPrimaryColor}
      secondaryCta={config.configuration.css.buttonSecondaryColor}
      {...props}
    />
  );
}

export function FormVerticalLinearStepper<
  T extends {
    content:
      | FunctionComponent<A>
      | ComponentClass<A, any>
      | ((x: A) => JSX.Element);
  },
  A extends {} = ComponentProps<T["content"]>
>(props: StepperPropTypes<T, A>) {
  const { config } = useContext(ConfigContext);
  return (
    <InnerFormVerticalLinearStepper
      background={config.configuration.css.formBackgroundColor}
      foreground={config.configuration.css.textColor}
      primaryCta={config.configuration.css.buttonPrimaryColor}
      secondaryCta={config.configuration.css.buttonSecondaryColor}
      {...props}
    />
  );
}
